import axios from 'axios';
import { adminApi, userApi, mainAdminApi } from "../../config";

const publicApi = userApi;

interface ProfileResponse {
  data: any;
}

export const formatProfileLocation = (location: unknown): string => {
  if (typeof location === "string") {
    const trimmedLocation = location.trim();

    if (!trimmedLocation) {
      return "";
    }

    if (trimmedLocation.startsWith("{") && trimmedLocation.endsWith("}")) {
      try {
        const parsedLocation = JSON.parse(trimmedLocation) as Record<string, unknown>;

        return [
          parsedLocation.address,
          parsedLocation.city,
          parsedLocation.state,
          parsedLocation.pincode,
          parsedLocation.country,
        ]
          .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
          .join(", ");
      } catch {
        return "";
      }
    }

    return trimmedLocation;
  }

  if (location && typeof location === "object" && !Array.isArray(location)) {
    const address = location as Record<string, unknown>;

    return [
      address.address,
      address.city,
      address.state,
      address.pincode,
      address.country,
    ]
      .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
      .join(", ");
  }

  return "";
};


export const fetchprofiledata = (): Promise<{ profile?: any; warning?: string }> => {
  return new Promise((resolve) => {
    adminApi.get<ProfileResponse>("/fetch-profile-data", { headers: { Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } })
      .then((res) => {
        resolve({ profile: res.data.data });
      })
      .catch((err) => {
        resolve({ warning: 'unauthenticate' });
      });
  });
};

export const updateDetails = async (form: any) => {
  const res = await adminApi.put("/instructor-update-profile", form, {
    headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` }
  });
  return res;
}

export const createNewClass = async (data: any) => {
  const res = await adminApi.post("/create-new-class", data, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } });
  return res.data;
}

export const fetchClasses = async () => {
  const res = await adminApi.get("/get-instructor-classes", { headers: { Authorization: `Bearer ${sessionStorage.getItem("mentorAccessToken")}` } });
  return res.data;
}

export const publicProfileData = async (slug: string) => {
  try {
    const cleanSlug = String(slug || "").trim();
    const res = await axios.get(`${publicApi.defaults.baseURL}/get-public-profile-data/${encodeURIComponent(cleanSlug)}`, {
      params: {
        _: Date.now(),
      },
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
    return res.data;
  } catch (err) {
    return { message: "unauthenticated" }
  }
}


export const deleteClass = async (classId: string) => {
  try {
    const res = await adminApi.delete(`/delete-class/${classId}`, { headers: { Authorization: `Bearer ${sessionStorage.getItem("mentorAccessToken")}` } });
    return res.data;
  } catch (err) {
    return { message: "Something went wrong, Please try Again!" };
  }
}

export const updateClass = async (classId: string, data: any) => {
  const res = await adminApi.put(`/update-class/${classId}`, data, { headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } });
  return res.data;
}

export const changePassword = async (data: any) => {
  try {
    const res = await adminApi.put("/change-password", data, { headers: { Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } })
    return res.data;
  } catch (err: any) {
    if (err.response.data.message) {
      return { message: err.response.data.message };
    }
  }
}

export const fetchSocialLinks = async () => {
  try {
    const res = await adminApi.get("/fetch-social-links", { headers: { Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } })
    return { socialLinks: res.data };
  } catch (err) {
    return { warning: 'unauthenticate' };
  }
}

export const updateSocialLinks = async (data: any) => {
  try {
    const res = await adminApi.put("/update-social-links", data, { headers: { Authorization: `Bearer ${sessionStorage.getItem('mentorAccessToken')}` } })
    return { success: true };
  } catch (err: any) {
    return { success: false, message: 'Failed to update social links.' };
  }
}

interface FetchBlogsResponse {
  blogs?: any[]; // you can refine type later
  warning?: string;
  error?: string;
}

export const fetchblogdata = async (mentorId: string) => {
  try {
    const res = await adminApi.get(`/my-blogs`, {
      headers: {
        "Content-Type": "application/json",
      },
      params: { mentorId }, // <-- use query params for GET requests
    });
    return res.data as FetchBlogsResponse;
  } catch (error: any) {
    console.error("Error fetching blogs:", error);
    return { warning: "Failed to fetch blogs" };
  }
};

export const fetchAllBlogs = async () => {
  try {
    const res = await adminApi.get("/get-blog");
    return res.data;
  } catch {
    return { warning: "Failed to fetch blogs" };
  }
};

export const deleteBlogApi = async (id: string) => {
  const res = await adminApi.delete(`/delete-blog/${id}`, {
    withCredentials: true,
  });
  return res.data;
};

export const updateBlogApi = async (id: string, payload: FormData) => {
  const res = await axios.put(`/update-blog/${id}`, payload, {
    withCredentials: true,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
  return res.data;
};

// profileAPIs.ts
export const fetchSingleBlogApi = async (id: string) => {
  const res = await adminApi.get(`/get-blog/${id}`);
  return res.data;
};

export const fetchEnrolledStudents = async (classId: string) => {
  try {
    console.log("Fetching enrolled students for classId:", classId);
    const token = sessionStorage.getItem('mentorAccessToken');
    console.log("Using token:", token ? "Yes" : "No");

    const res = await adminApi.get(`/class-enrolled-students/${classId}`);

    console.log("API Response status:", res.status);
    console.log("API Response data:", res.data);

    return res.data;
  } catch (error: any) {
    console.error("Error fetching enrolled students:", error);
    console.error("Error response:", error.response?.data);
    console.error("Error message:", error.message);

    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to fetch enrolled students",
      data: []
    };
  }
};

export const fetchTodaysSchedule = async () => {
  try {
    console.log("Fetching today's schedule");
    const token = sessionStorage.getItem('mentorAccessToken');
    console.log("Using token:", token ? "Yes" : "No");

    const res = await adminApi.get(`/todays-schedule`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    console.log("Today's Schedule API Response:", res.data);

    return res.data;
  } catch (error: any) {
    console.error("Error fetching today's schedule:", error);
    console.error("Error response:", error.response?.data);

    return {
      success: false,
      message: error.response?.data?.message || error.message || "Failed to fetch today's schedule",
      data: { classes: [] }
    };
  }
};


export interface OTPResponse {
  success: boolean;
  message: string;
  verified?: boolean;
  attemptsRemaining?: number;
}

/**
 * Send OTP to phone number
 */
export const sendOtp = async (phone: string): Promise<OTPResponse> => {
  try {
    console.log("[API] Calling send-otp at:", `${publicApi.defaults.baseURL}/auth/send-otp`);
    const res = await publicApi.post<OTPResponse>('/auth/send-otp', { phone });
    return res.data;
  } catch (err: any) {
    console.error('[API] Send OTP Error:', err.response?.data || err.message);
    return {
      success: false,
      message: err.response?.data?.message || 'Failed to send OTP',
    };
  }
};

/**
 * Verify OTP
 */
export const verifyOtp = async (phone: string, otp: string): Promise<OTPResponse> => {
  try {
    const res = await publicApi.post<OTPResponse>('/auth/verify-otp', { phone, otp });
    return res.data;
  } catch (err: any) {
    console.error('[API] Verify OTP Error:', err.response?.data || err.message);
    return {
      success: false,
      message: err.response?.data?.message || 'OTP verification failed',
    };
  }
};


interface PopupMobileResponse {
  message: string;
  success?: boolean;
}

// Popup mobile submit API
export const submitPopupMobile = async (
  mobile: string
): Promise<PopupMobileResponse> => {
  try {
    const res = await publicApi.post<PopupMobileResponse>("/user/api/v1/save-popup-mobile", {
      mobile,
    });

    return res.data as PopupMobileResponse;
  } catch (err: any) {
    if (err.response?.data?.message) {
      return { message: err.response.data.message };
    }

    return { message: "Something went wrong. Please try again." };
  }
};
